/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react'
import {
  Controller,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Dropdown,
  FormControl,
  InputGroup,
  TextareaInput,
  Loader,
  TicketImageUpload,
} from 'components'
import { findSelected, trimFirstSpace } from 'helpers/common'
import { findAllTaskPendingReasons } from 'features/Ticket'
import { OptionProps } from 'types'

import type { ISchemaFormPendingTicket } from '../model'

interface TicketPendingModalFormProps {
  payloadBeforeSubmit?: ISchemaFormPendingTicket
  setPayloadBeforeSubmit: (payload: any) => void
}

function TicketPendingModalForm(
  props: TicketPendingModalFormProps,
): JSX.Element {
  const { payloadBeforeSubmit, setPayloadBeforeSubmit } = props

  const [checkIsPendingReasons, setCheckIsPendingReasons] =
    useState<boolean>(true)
  const [pendingReasons, setPendingReasons] = useState<OptionProps[]>([])

  const { t } = useTranslation('ticket')
  const { control, errors, setValue, trigger } = useFormContext()

  const base = 'f-ticket-pending-modal'

  useEffect(() => {
    setCheckIsPendingReasons(true)

    fetchPendingReasons()
  }, [])

  const fetchPendingReasons = async () => {
    const { data } = await findAllTaskPendingReasons()
    const taskPendingReasons: OptionProps[] =
      data?.map((taskPendingReason) => ({
        key: taskPendingReason.id,
        label: taskPendingReason.reasonTh || '',
      })) || []

    setPendingReasons(taskPendingReasons)
    setCheckIsPendingReasons(false)
  }

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    fields: ControllerRenderProps<Record<string, File>>,
  ) => {
    const files = Array.from(event.target.files || [])
    const currentImages = Array.isArray(fields.value) ? fields.value : []
    const newImages = [...currentImages, ...files]
    const limitedImages = newImages.slice(0, 5)

    fields.onChange(limitedImages)
    setPayloadBeforeSubmit({
      ...payloadBeforeSubmit,
      pendingPhoto: limitedImages,
    })
  }

  const handleImageClear = (
    index: number,
    fields: ControllerRenderProps<Record<string, File>>,
  ) => {
    const newImages = fields.value.filter((_: File, i: number) => i !== index)

    fields.onChange(newImages)
    setPayloadBeforeSubmit({
      ...payloadBeforeSubmit,
      pendingPhoto: newImages,
    })
  }

  return (
    <div className={`${base}__form`}>
      <FormControl label={t('reason.title')} required>
        <InputGroup error={errors.pendingReason?.message} variants='secondary'>
          <Controller
            type='text'
            name='pendingReason'
            control={control}
            defaultValue=''
            isError={Boolean(errors.pendingReason?.message)}
            message={errors.pendingReason?.message}
            render={(field) => (
              <Dropdown
                className={`${base}__form-item`}
                placeholder={t('selectReason.placeholder')}
                withIcon={checkIsPendingReasons && <Loader />}
                isError={Boolean(errors.pendingReason?.message)}
                options={pendingReasons}
                onChange={(value) => {
                  setValue('pendingReason', value.key, { shouldDirty: true })
                  setPayloadBeforeSubmit({
                    ...payloadBeforeSubmit,
                    pendingReason: value.key,
                  })
                }}
                onBlur={async () => {
                  await trigger('pendingReason')
                }}
                data-testid={'TicketAssignForm-Dropdown-Reason'}
                selected={findSelected(pendingReasons, field.value)}
              />
            )}
          />
        </InputGroup>
      </FormControl>

      <FormControl label={t('remark.title')}>
        <Controller
          name='pendingDetail'
          control={control}
          defaultValue=''
          render={(fields) => (
            <TextareaInput
              placeholder={t('insertDetail.placeholder')}
              className={`${base}__form-item`}
              hasCountable
              {...fields}
              onChange={(e) => {
                fields.onChange(trimFirstSpace(e))
                setPayloadBeforeSubmit({
                  ...payloadBeforeSubmit,
                  pendingDetail: trimFirstSpace(e),
                })
              }}
            />
          )}
        />
      </FormControl>

      <FormControl label={t('images.title')} recommend={t('images.recommend')}>
        <Controller
          name='pendingPhoto'
          control={control}
          defaultValue={null}
          render={(fields) => {
            return (
              <TicketImageUpload
                images={fields.value || []}
                onAddImages={(event) => handleImageChange(event, fields)}
                onRemoveImage={(index) => handleImageClear(index, fields)}
                className={`${base}__form-item`}
              />
            )
          }}
        />
      </FormControl>
    </div>
  )
}

export default TicketPendingModalForm
